import { FC, useEffect, useRef, useState } from 'react';
import { ReactSVG } from 'react-svg';
import classNames from 'classnames';
import { LoadingIndicator } from '@forma/forma-ui-kit';

import styles from './video.module.css';

interface VideoProps {
  src: string,
  className?: string,
  playClassName?: string,
  controls?: boolean,
  autoPlay?: boolean,
  loop?: boolean,
  muted?: boolean,
  onPlay?: () => void
}

const Video: FC<VideoProps> = ({ className, playClassName, controls = true, autoPlay, muted = false, src, loop, onPlay }) => {
  const ref = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [canPlay, setCanPlay] = useState(false);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);

  useEffect(() => {
    if (!videoRef.current) return;
    if (videoRef.current.readyState === videoRef.current.HAVE_ENOUGH_DATA) setCanPlay(true);
  }, [videoRef]);

  const handleClickPlay = () => {
    if (!videoRef.current) return;
    videoRef.current.play();
    if (onPlay) onPlay();
  };

  return (
    <div className={classNames(styles.root, className)} ref={ref}>
      {!canPlay ? (
        <div className={styles.loader}>
          <LoadingIndicator color="var(--primary-color)" />
        </div>
      ) : (
        (controls && !isPlaying) && (
          <button className={classNames(styles.playButton, playClassName)} onClick={handleClickPlay}>
            <ReactSVG src="/icons/play-large.svg" wrapper="span" />
          </button>
        )
      )}
      <video
        className={styles.video} src={src}
        controls={controls && isPlaying}
        onPlay={() => { setCanPlay(true); setIsPlaying(true); }}
        onCanPlay={() => setCanPlay(true)}
        onCanPlayThrough={() => setCanPlay(true)}
        onLoadedMetadata={() => setCanPlay(true)}
        onPause={(e) => {
          const target = e.target as HTMLVideoElement;
          if (target.readyState === 1) return;
          setIsPlaying(false);
        }}
        autoPlay={autoPlay}
        muted={muted}
        loop={loop}
        ref={videoRef}
      />
    </div>
  );
};

export default Video;
